'use client';

import { useEffect, useId, useRef } from 'react';

export function NewLectureDialog({ onCancel, onConfirm }: {
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    dialog.showModal();
    cancelRef.current?.focus();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      dialog.close();
      document.body.style.overflow = overflow;
    };
  }, []);

  const cancel = () => {
    // Close while connected so native focus restoration returns to the trigger.
    dialogRef.current?.close();
    onCancel();
  };

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      aria-modal="true"
      className="fixed inset-0 m-auto w-[calc(100%-2rem)] max-w-md rounded-2xl border border-border bg-card p-6 text-foreground shadow-2xl backdrop:bg-black/50 backdrop:backdrop-blur-[1px] sm:p-8"
      onCancel={event => { event.preventDefault(); cancel(); }}
      onKeyDown={event => event.stopPropagation()}
    >
      <h2 id={titleId} className="font-serif text-2xl font-semibold">Start a new lecture?</h2>
      <p id={descriptionId} className="mt-3 text-sm leading-6 text-muted">
        This lecture isn’t finished. Leave the remaining cards unreviewed and return to upload?
        Your review decisions so far will be preserved.
      </p>
      <div className="mt-6 flex flex-wrap justify-end gap-3">
        <button ref={cancelRef} type="button" onClick={cancel} className="ui-button ui-button--secondary">Keep reviewing</button>
        <button type="button" onClick={onConfirm} className="ui-button ui-button--primary">Start new lecture</button>
      </div>
    </dialog>
  );
}
